/**
 * Lo que el navegador muestra de una marca ANTES de que cargue React: el título
 * de la pestaña, la descripción, el favicon, el logo que se precarga y el
 * manifest.
 *
 * Vive aparte y sin imports porque lo leen dos mundos: `vite.config.ts`, que lo
 * escribe en `index.html` al construir (SL-1921, ADR-044), y el registro de
 * marcas, que exige una entrada por `TemplateId` (ver `registry.ts`). Importar
 * cualquier otra cosa acá metería AntD en la config de Vite.
 *
 * Las rutas son relativas a `PUBLIC_URL` y empiezan con `/`.
 */

export type BrandMeta = {
  title: string;
  description: string;
  favicon: string;
  /** El logo que `index.html` precarga para el primer paint. */
  logo: string;
  manifest: string;
  /** Lo que el login y la pantalla de contraseña muestran como sitio de la marca. */
  website: string;
  /** Slogan, solo si la marca tiene uno propio. */
  tagline?: string;
};

const SILIA_META: BrandMeta = {
  title: 'Silia',
  description:
    'Silia — Enterprise conversational assistant platform. Create and manage AI-powered chatbots for your business.',
  favicon: '/favicon.ico',
  logo: '/assets/icons/Logo-Silia.svg',
  manifest: '/manifest.json',
  website: 'Silia.com',
  tagline: 'Born from the spirit of discovery',
};

export const BRAND_META = {
  default: SILIA_META,
  // Traxión no tiene archivos propios todavía (ver `assets.ts`): hereda los de
  // Silia, igual que sus logos.
  traxion: SILIA_META,
  prismhr: {
    title: 'PrismHR',
    description: 'PrismHR — conversational assistants.',
    favicon: '/assets/icons/LogoMobilePrismHR.svg',
    logo: '/assets/icons/Logo-PrismHR.svg',
    manifest: '/manifest-prismhr.json',
    website: 'prismhr.com',
  },
} as const satisfies Record<string, BrandMeta>;

/**
 * Cómo se lee un id de marca que viene de afuera (`REACT_APP_THEME`, un
 * subdominio, localStorage): sin espacios y en minúsculas.
 *
 * Es la ÚNICA implementación. La usan `brandMetaFor` (lo que Vite escribe en
 * `index.html`) y `normalizeBrandId` (la marca que pinta React). Si cada uno
 * normalizara por su cuenta, un cambio en uno solo haría que el HTML dijera
 * PrismHR y la app pintara Silia.
 */
export const normalizeThemeId = (value: unknown): string =>
  typeof value === 'string' ? value.trim().toLowerCase() : '';

/**
 * La meta de la marca que pide `REACT_APP_THEME`, o la de Silia si el valor no
 * es una marca.
 */
export const brandMetaFor = (themeEnv: string | undefined): BrandMeta => {
  const id = normalizeThemeId(themeEnv);
  return Object.prototype.hasOwnProperty.call(BRAND_META, id)
    ? BRAND_META[id as keyof typeof BRAND_META]
    : BRAND_META.default;
};
